import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";

import { AppBar, Container, Dialog, Grid, IconButton, Toolbar, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AsolviDatePicker } from "components/AsolviDatePicker";
import { AsolviSwitch } from "components/AsolviSwitch";
import { AsolviTimePicker } from "components/AsolviTimePicker";
import { AvailabilityTypeAutoComplete } from "components/AvailabilityTypeAutocomplete";
import PrimaryButton from "components/PrimaryButton";
import SecondaryGreyButton from "components/SecondaryGreyButton";
import StyledTextField from "components/StyledTextField";
import { DefinedDate, toDate, toDateString } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { AvailabilityValidationSchema } from "models/validation/AvailabilityValidationSchema";
import { AvailabilityType, UpdateAvailabilityInputType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  getMobileCalendar,
  setEditAvailability,
  updateAvailability,
} from "store/slices/calendar.store";
import { setOpen } from "store/slices/dialog.store";
import { ValidationError } from "yup";
const PREFIX = "EditAvailabilityDialog";

const classes = {
  flex: `${PREFIX}-flex`,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(2),

  [`& .${classes.flex}`]: {
    marginTop: theme.spacing(2),
    display: "flex",
  },
}));

export const EditAvailabilityDialog: FC = (props) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.dialog.availability);
  const { editAvailability } = useAppSelector((s) => s.calendar.mobileCalendar);
  const { availabilityTypes } = useAppSelector((s) => s.cache);

  const [errors, setErrors] = useState<any>({});
  const [selectedType, setSelectedType] = useState<AvailabilityType | null>(null);
  const [startTime, setStartTime] = useState<DefinedDate>(toDateString(new Date()));
  const [stopTime, setStopTime] = useState<DefinedDate>(toDateString(new Date()));
  const [description, setDescription] = useState<string>("");
  const [allDay, setAllDay] = useState<boolean>(true);

  const reset = useCallback(() => {
    const date = new Date();
    setSelectedType(null);
    setStartTime(toDateString(date));
    date.setMinutes(date.getMinutes() + 30);
    setStopTime(date);
    setDescription("");
    setAllDay(true);
  }, []);

  useEffect(() => {
    if (open) {
      if (editAvailability) {
        setSelectedType(
          availabilityTypes.find((x) => x.code === editAvailability.availabilityType) ?? null,
        );
        setDescription(editAvailability.barText ?? "");
        setAllDay(false);
        setStartTime(new Date(editAvailability.dateFrom!));
        setStopTime(new Date(editAvailability.dateTo!));
      } else {
        reset();
      }
    }
  }, [availabilityTypes, editAvailability, open, reset]);

  const handleClose = useCallback(() => {
    reset();
    dispatch(setOpen({ dialogName: "availability", open: false }));
    dispatch(setEditAvailability({ availability: null }));
  }, [dispatch, reset]);

  const handleSave = useCallback(
    (isDelete: boolean) => {
      const dateFrom = new Date(startTime);
      const dateTo = new Date(stopTime);
      if (allDay) {
        const workStart = technicianSettings.workTimeStart.split(":");
        const workEnd = technicianSettings.workTimeEnd.split(":");
        dateFrom.setHours(parseInt(workStart[0]), parseInt(workStart[1]), 0);
        dateTo.setHours(parseInt(workEnd[0]), parseInt(workEnd[1]), 0);
      }
      const request: UpdateAvailabilityInputType = {
        availabilityType: selectedType?.code,
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
        description: description,
        availNo: editAvailability?.availNo ?? null,
        isDelete: isDelete,
      };
      dispatch(updateAvailability({ availability: request })).then(() => {
        dispatch(getMobileCalendar());
      });
      setErrors({});
      handleClose();
    },
    [
      allDay,
      editAvailability?.availNo,
      description,
      dispatch,
      handleClose,
      selectedType,
      startTime,
      stopTime,
    ],
  );

  return (
    <Dialog fullScreen open={open} onClose={handleClose} data-testid="EditAvailabilityDialog">
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="EditAvailabilityDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" marginLeft="16px" flex={1}>
            {editAvailability?.availNo && <FormattedMessage id="availability.edit" />}
            {(!editAvailability || !editAvailability.availNo) && (
              <FormattedMessage id="availability.add" />
            )}
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <AvailabilityTypeAutoComplete
              value={selectedType}
              onChange={(value) => {
                setSelectedType(value);
                setDescription(value?.description ?? "");
              }}
            />
          </Grid>
          <Grid item container direction="row" spacing={1} wrap="nowrap">
            <Grid item xs={6}>
              <AsolviDatePicker
                error={errors?.startDate}
                name={`NewAvailabilityStartDate`}
                label={intl.formatMessage({ id: "times.startDate" })}
                required
                value={toDate(startTime)}
                onChange={(value: Date) => setStartTime(toDateString(value))}
              />
            </Grid>
            <Grid item xs={6}>
              <AsolviDatePicker
                error={errors?.endDate}
                name={`NewAvailabilityEndDate`}
                label={intl.formatMessage({ id: "times.endDate" })}
                required
                value={toDate(stopTime)}
                onChange={(value: Date) => setStopTime(toDateString(value))}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <StyledTextField
              fullWidth
              value={description}
              id="description"
              label={intl.formatMessage({ id: "availability.description" })}
              onChange={(e: any) => {
                const { value } = e.target;
                setDescription(value);
              }}
              multiline
              rows={5}
            />
          </Grid>

          <Grid item container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="body1">
                <FormattedMessage id="availability.allDayToggle" />
              </Typography>
            </Grid>
            <Grid item>
              <AsolviSwitch
                key="allDay"
                checked={allDay}
                onClick={() => {
                  setAllDay(!allDay);
                }}
                data-testid="EditAvailabilityDialog-Toggle"
                disabled={Boolean(editAvailability) && editAvailability?.availNo !== null}
              />
            </Grid>
          </Grid>

          {!allDay && (
            <Grid item container direction="row" spacing={1} wrap="nowrap">
              <Grid item xs={6}>
                <AsolviTimePicker
                  error={errors?.startTime}
                  name={`NewAvailabilityStartTime`}
                  label={intl.formatMessage({ id: "times.startTime" })}
                  required
                  value={toDate(startTime)}
                  onChange={(value: Date) => setStartTime(toDateString(value))}
                  disabled={allDay}
                  readOnly={allDay}
                />
              </Grid>
              <Grid item xs={6}>
                <AsolviTimePicker
                  error={errors?.stopTime}
                  name={`NewAvailabilityEndTime`}
                  label={intl.formatMessage({ id: "times.endTime" })}
                  required
                  value={toDate(stopTime)}
                  onChange={(value: Date) => setStopTime(toDateString(value))}
                  disabled={allDay}
                  readOnly={allDay}
                />
              </Grid>
            </Grid>
          )}

          <Grid item>
            <Grid xs={12}>
              <SecondaryGreyButton onClick={() => handleClose()}>
                {intl.formatMessage({ id: "general.cancel" })}
              </SecondaryGreyButton>
            </Grid>

            <Grid xs={12}>
              <PrimaryButton
                key="EditAvailabilityDialog-SaveButton"
                disabled={!selectedType}
                onClick={() => {
                  const newAvailability = {
                    type: selectedType,
                    startTime: toDateString(startTime),
                    stopTime: toDateString(stopTime),
                    allDay: allDay,
                    description,
                  };

                  const validationSchema = AvailabilityValidationSchema();
                  const isValid = validationSchema.isValidSync(newAvailability);
                  if (isValid) {
                    handleSave(false);
                  } else {
                    try {
                      validationSchema.validateSync(newAvailability, {
                        strict: true,
                        abortEarly: false,
                      });
                    } catch (e: any) {
                      let { inner } = e as ValidationError;
                      let errors: any = {};
                      for (let error of inner) {
                        if (!error.path) continue;
                        errors[error.path] = error.message;
                      }
                      setErrors({ ...errors });
                    }
                  }
                }}
              >
                <FormattedMessage id="general.save" />
              </PrimaryButton>
            </Grid>
          </Grid>
        </Grid>
      </StyledContainer>
    </Dialog>
  );
};
